import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export type SettingsSection =
    | 'appearance'
    | 'crochet'
    | 'account'
    | 'billing'
    | 'offlineApp'
    | 'yourData'
    | 'privacy'
    | 'hotkeys'
    | 'info'
    | 'connectAi';

/** Every settings section, in the order the route validates them (not nav order). */
export const ALL_SETTINGS_SECTIONS: readonly SettingsSection[] = [
    'appearance',
    'crochet',
    'account',
    'billing',
    'offlineApp',
    'yourData',
    'privacy',
    'hotkeys',
    'info',
    'connectAi',
];

const NAV_ITEMS: { section: SettingsSection; labelKey: string }[] = [
    { section: 'appearance', labelKey: 'settingsPage.nav.appearance' },
    { section: 'crochet', labelKey: 'settingsPage.nav.crochet' },
    { section: 'account', labelKey: 'settingsPage.nav.account' },
    { section: 'privacy', labelKey: 'consent.settings.navLabel' },
    { section: 'hotkeys', labelKey: 'settingsPage.nav.hotkeys' },
    { section: 'info', labelKey: 'settingsPage.nav.info' },
    { section: 'billing', labelKey: 'settingsPage.nav.billing' },
    { section: 'offlineApp', labelKey: 'settingsPage.nav.offlineApp' },
    { section: 'yourData', labelKey: 'settingsPage.nav.yourData' },
    { section: 'connectAi', labelKey: 'settingsPage.nav.connectAi' },
];

interface SettingsNavProps {
    active: SettingsSection;
    onSelect: (section: SettingsSection) => void;
    /** False hides the Billing entry (the `billing` gate is closed). */
    showBilling: boolean;
    /** False hides the Offline app entry (the `offline_app` gate is closed). */
    showOfflineApp: boolean;
    /** False hides the Your data entry. The settings page always passes true: Your data is offered to everyone. */
    showYourData: boolean;
    /** Only these sections get an entry (the gate flags above still apply on top). Defaults to all. */
    sections?: readonly SettingsSection[];
}

/** Sidebar (on md+) / tab row (on mobile) for switching settings sections. */
export function SettingsNav({
    active,
    onSelect,
    showBilling,
    showOfflineApp,
    showYourData,
    sections = ALL_SETTINGS_SECTIONS,
}: SettingsNavProps) {
    const { t } = useTranslation();
    const items = NAV_ITEMS.filter(
        ({ section }) =>
            sections.includes(section) &&
            (section !== 'billing' || showBilling) &&
            (section !== 'offlineApp' || showOfflineApp) &&
            (section !== 'yourData' || showYourData),
    );

    return (
        <nav className="flex gap-1 md:w-48 md:flex-col" aria-label={t('settingsPage.title')}>
            {items.map(({ section, labelKey }) => {
                const isActive = section === active;
                return (
                    <button
                        key={section}
                        type="button"
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => onSelect(section)}
                        className={cn(
                            'rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                            isActive
                                ? 'bg-accent text-accent-foreground'
                                : 'text-foreground/60 hover:bg-accent hover:text-accent-foreground',
                        )}
                    >
                        {t(labelKey)}
                    </button>
                );
            })}
        </nav>
    );
}
